'use client';

import { toast as sonnerToast } from 'sonner';
import { createElement } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  Loader2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

type Variant = 'default' | 'success' | 'error' | 'warning' | 'info' | 'destructive' | 'loading';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastOptions {
  title?: string;
  description?: string;
  duration?: number;
  action?: ToastAction;
  variant?: Variant;
}

const variantStyles: Record<Variant, string> = {
  default: 'bg-card border-border text-foreground',
  info: 'bg-card border-blue-600/50',
  success: 'bg-card border-green-600/50',
  error: 'bg-card border-destructive/50',
  destructive: 'bg-card border-destructive/50',
  warning: 'bg-card border-amber-600/50',
  loading: 'bg-card border-primary/50',
};

const titleColor: Record<Variant, string> = {
  default: 'text-foreground',
  info: 'text-blue-600 dark:text-blue-400',
  success: 'text-green-600 dark:text-green-400',
  error: 'text-destructive',
  destructive: 'text-destructive',
  warning: 'text-amber-600 dark:text-amber-400',
  loading: 'text-primary',
};

const iconColor: Record<Variant, string> = {
  default: 'text-muted-foreground',
  info: 'text-blue-600 dark:text-blue-400',
  success: 'text-green-600 dark:text-green-400',
  error: 'text-destructive',
  destructive: 'text-destructive',
  warning: 'text-amber-600 dark:text-amber-400',
  loading: 'text-primary',
};

const variantIcons: Record<Variant, React.ComponentType<{ className?: string }>> = {
  default: Info,
  info: Info,
  success: CheckCircle,
  error: AlertCircle,
  destructive: AlertCircle,
  warning: AlertTriangle,
  loading: Loader2,
};

const toastAnimation = {
  initial: { opacity: 0, y: -20, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -20, scale: 0.95 },
};

function createToast(
  message: string | ToastOptions,
  variant: Variant = 'default',
  extraOptions?: ToastOptions
) {
  // Handle both signatures: 
  // - toast('message') 
  // - toast('message', { action: ... })
  // - toast({ title, description, variant }) - Radix-style
  let options: ToastOptions;
  let effectiveVariant = variant;
  
  if (typeof message === 'string') {
    options = { description: message, ...extraOptions };
  } else {
    options = message;
    // If variant is passed in options (Radix-style), use it
    if (options.variant) {
      effectiveVariant = options.variant;
    }
  }

  const { title, description, duration = effectiveVariant === 'loading' ? Infinity : 4000, action } = options;
  const Icon = variantIcons[effectiveVariant];
  const isLoading = effectiveVariant === 'loading';

  return sonnerToast.custom(
    (toastId) =>
      createElement(
        motion.div,
        {
          ...toastAnimation,
          className: cn(
            'relative flex w-full max-w-sm flex-col gap-2 rounded-lg border p-4 pr-10 shadow-lg',
            variantStyles[effectiveVariant]
          ),
        },
        // X button - positioned absolute top-right
        !isLoading &&
          createElement(
            'button',
            {
              onClick: () => sonnerToast.dismiss(toastId),
              className:
                'absolute top-3 right-3 rounded-full p-1 hover:bg-muted/50 transition-colors focus:outline-none',
              'aria-label': 'Dismiss notification',
            },
            createElement(X, { className: 'h-4 w-4 text-muted-foreground' })
          ),
        // Content with icon and text
        createElement(
          'div',
          { className: 'flex items-start gap-3' },
          createElement(Icon, {
            className: cn(
              'mt-0.5 h-5 w-5 shrink-0',
              iconColor[effectiveVariant],
              isLoading && 'animate-spin'
            ),
          }),
          createElement(
            'div',
            { className: 'flex-1 space-y-1' },
            title &&
              createElement(
                'p',
                {
                  className: cn('text-sm font-semibold', titleColor[effectiveVariant]),
                },
                title
              ),
            description &&
              createElement(
                'p',
                { className: 'text-sm text-muted-foreground' },
                description
              )
          )
        ),
        // Action button (if present)
        action &&
          createElement(
            'div',
            { className: 'flex items-center justify-end' },
            createElement(
              'button',
              {
                onClick: () => {
                  action.onClick();
                  sonnerToast.dismiss(toastId);
                },
                className: cn(
                  'text-xs font-medium px-3 py-1.5 rounded-md border transition-colors cursor-pointer',
                  effectiveVariant === 'success'
                    ? 'text-green-600 border-green-600 hover:bg-green-600/10 dark:text-green-400 dark:border-green-400'
                    : effectiveVariant === 'error' || effectiveVariant === 'destructive'
                    ? 'text-destructive border-destructive hover:bg-destructive/10'
                    : effectiveVariant === 'warning'
                    ? 'text-amber-600 border-amber-600 hover:bg-amber-600/10 dark:text-amber-400 dark:border-amber-400'
                    : effectiveVariant === 'info'
                    ? 'text-blue-600 border-blue-600 hover:bg-blue-600/10 dark:text-blue-400 dark:border-blue-400'
                    : 'text-foreground border-border hover:bg-muted/50'
                ),
              },
              action.label
            )
          )
      ),
    { duration }
  );
}

function toast(message: string | ToastOptions, options?: ToastOptions) {
  return createToast(message, 'default', options);
}

toast.success = (message: string | ToastOptions, options?: ToastOptions) => {
  return createToast(message, 'success', options);
};

toast.error = (message: string | ToastOptions, options?: ToastOptions) => {
  return createToast(message, 'error', options);
};

toast.warning = (message: string | ToastOptions, options?: ToastOptions) => {
  return createToast(message, 'warning', options);
};

toast.info = (message: string | ToastOptions, options?: ToastOptions) => {
  return createToast(message, 'info', options);
};

toast.loading = (message: string | ToastOptions, options?: ToastOptions) => {
  return createToast(message, 'loading', options);
};

type PromiseMessage<T> = string | ToastOptions | ((data: T) => string | ToastOptions);

interface PromiseMessages<T> {
  loading: string | ToastOptions;
  success: PromiseMessage<T>;
  error: PromiseMessage<unknown>;
}

toast.promise = <T>(
  promise: Promise<T>,
  messages: PromiseMessages<T>
): Promise<T> => {
  const loadingId = toast.loading(messages.loading);

  promise
    .then((data) => {
      sonnerToast.dismiss(loadingId);
      const successMessage = typeof messages.success === 'function'
        ? messages.success(data)
        : messages.success;
      toast.success(successMessage);
    })
    .catch((error) => {
      sonnerToast.dismiss(loadingId);
      const errorMessage = typeof messages.error === 'function'
        ? messages.error(error)
        : messages.error;
      toast.error(errorMessage);
    });

  return promise;
};

toast.dismiss = sonnerToast.dismiss;

export { toast };
